import React, {useState} from 'react';

import {ANALYTICS_MODULE} from 'analytics/AnalyticsTypes';
import {OrganizationDefaultSchemaCollaboratorsConfiguration} from 'auth/__generated/OrganizationDefaultSchemaCollaboratorsConfiguration';
import {Collaborator, SchemaCollaborator} from 'auth/PermissionConstants';
import {useSchemaCollaboratorsSectionWithDefaults} from 'collaborator-components/useSchemaCollaboratorsSection';
import {InlineBannerProps} from 'ui-library/banners/InlineBanner';
import {ButtonStyle} from 'ui-library/buttons/Button';
import Modal from 'ui-library/modals/Modal';

enum SchemaCreateKey {
  METADATA = 'METADATA',
  ACCESS_POLICIES = 'ACCESS_POLICIES',
}

export interface CreateSchemaModalProps {
  title: string;
  newFieldNames: Array<string>;
  onRequestClose: () => void;
  organization: OrganizationDefaultSchemaCollaboratorsConfiguration;
  createSchema: (collaborators: Array<SchemaCollaborator>) => void;
  renderMetadataSection: () => JSX.Element;
  permissionsSettingsClass: React.ComponentType<{
    collaborators: Array<Collaborator>;
    isReadOnly: boolean;
    onUpdate: (collaborators: Array<Collaborator>) => void;
  }>;
  isSchemaPermissionsEnabled: boolean;
  bannerProps: InlineBannerProps | null;
  performValidation: () => boolean;
}

export default function CreateSchemaModal(props: CreateSchemaModalProps): JSX.Element {
  const [{collaborators, minimalCollaborators}, handleUpdate] = useSchemaCollaboratorsSectionWithDefaults(
    props.organization
  );
  const [paneKey, setPaneKey] = useState(SchemaCreateKey.METADATA);

  const modalButtons = [];
  if (paneKey === SchemaCreateKey.ACCESS_POLICIES) {
    modalButtons.push({
      style: ButtonStyle.SECONDARY,
      submit: false,
      disabled: false,
      text: 'Previous',
      onClick: () => {
        setPaneKey(SchemaCreateKey.METADATA);
      },
    });
  }
  if (paneKey === SchemaCreateKey.ACCESS_POLICIES || !props.isSchemaPermissionsEnabled) {
    modalButtons.push({
      style: ButtonStyle.SECONDARY,
      submit: false,
      disabled: props.bannerProps != null,
      text: props.title,
      onClick: () => {
        const isValid = props.performValidation();
        if (!isValid) {
          return;
        }
        props.createSchema(minimalCollaborators);
      },
    });
  } else {
    modalButtons.push({
      style: ButtonStyle.SECONDARY,
      submit: false,
      disabled: props.bannerProps != null,
      text: 'Next',
      onClick: () => {
        const isValid = props.performValidation();
        if (!isValid) {
          return;
        }
        setPaneKey(SchemaCreateKey.ACCESS_POLICIES);
      },
    });
  }

  return (
    <Modal
      title={props.title}
      fullWidth={true}
      buttons={modalButtons}
      bannerProps={props.bannerProps}
      onRequestClose={props.onRequestClose}
      analyticsModule={ANALYTICS_MODULE.SCHEMAS}
      analyticsModalType="create_schema"
    >
      {paneKey === SchemaCreateKey.METADATA && props.renderMetadataSection()}
      {paneKey === SchemaCreateKey.ACCESS_POLICIES && (
        <props.permissionsSettingsClass
          isReadOnly={false}
          collaborators={collaborators}
          onUpdate={handleUpdate}
        />
      )}
    </Modal>
  );
}
